export function captureInlineStyles(element) {
  let styles = [...element.style].map(property => ({
    property,
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
  }))

  return () => {
    element.removeAttribute('style')
    for (let { property, value, priority } of styles) {
      element.style.setProperty(property, value, priority)
    }
  }
}

let implicitQueueKeys = new WeakMap()
let queues = new Map()

async function animate(element, values, options = {}) {
  let {
    // Callbacks
    onCapture,
    onBeforeAnimate,
    onAnimate,
    onAbort,
    onFinish,
    onComplete,
    onUnsupported,

    // Options
    queue,
    queued = false,
    reverse = false,
    persist = false,
    duration = 500,
    easing = 'ease',
    signal,
    abortAction = 'finish',
  } = options

  let supported = typeof element.animate === 'function'
  let shouldCancel = signal?.aborted

  if (shouldCancel || !supported) {
    if (persist && !reverse) {
      Object.assign(element.style, values)
    }

    if (!supported) {
      onUnsupported?.(element)
    }

    return
  }

  if (typeof queue === 'undefined') {
    if (implicitQueueKeys.has(element)) {
      queue = implicitQueueKeys.get(element)
    } else {
      queue = Symbol('animation queue')
      implicitQueueKeys.set(element, queue)
    }
  }

  if (queue !== false) {
    if (typeof queue !== 'string' && typeof queue !== 'symbol') {
      throw new Error('Invalid "queue" option')
    }

    if (!queues.has(queue)) {
      queues.set(queue, [])
    }

    if (!queued && queues.get(queue).length !== 0) {
      return new Promise(resolve => {
        queues
          .get(queue)
          .push(() => animate(element, values, { ...options, queued: true }).then(resolve))
      })
    } else {
      // We need to unshift anything to the queue to make future animate()
      // calls queue up correctly
      queues.get(queue).unshift(null)
    }
  }

  await onCapture?.(element)
  let restoreInlineStyles = captureInlineStyles(element)

  let properties = Object.keys(values)
  let computedStyle = getComputedStyle(element)

  let startStyle = Object.fromEntries(
    properties.map(property => [property, computedStyle[property]]),
  )

  Object.assign(element.style, values)

  let computedStyle2 = getComputedStyle(element)
  let endStyle = Object.fromEntries(
    properties.map(property => [property, computedStyle2[property]]),
  )

  restoreInlineStyles()

  await onBeforeAnimate?.(element)

  let animation = element.animate(reverse ? [endStyle, startStyle] : [startStyle, endStyle], {
    duration,
    easing,
  })

  onAnimate?.(element, animation)

  if (signal) {
    let listener = () => {
      if (abortAction === 'cancel') {
        animation.pause()
        let styles = Object.fromEntries(
          properties.map(property => [property, computedStyle[property]]),
        )
        animation.cancel()
        Object.assign(element.style, styles)
      } else {
        animation.finish()
      }

      onAbort?.(element)
    }

    signal.addEventListener('abort', listener)
    animation.finished
      .catch(() => {})
      .then(() => {
        signal.removeEventListener('abort', listener)
      })
  }

  await animation.finished
    .then(() => {
      onFinish?.(element)
    })
    .catch(() => {})

  onComplete?.(element)

  if (persist && !reverse && abortAction !== 'cancel') {
    Object.assign(element.style, values)
  }

  if (queue !== false) {
    queues.get(queue).shift()

    if (queues.get(queue).length > 0) {
      let next = queues.get(queue).shift()
      next()
    } else {
      queues.delete(queue)
    }
  }
}

export function from(element, values, options = {}) {
  return animate(element, values, {
    ...options,
    reverse: true,
  })
}

export function to(element, values, options = {}) {
  return animate(element, values, {
    ...options,
    reverse: false,
  })
}

export async function slideUp(element, { duration = 600, easing = 'ease', queue } = {}) {
  let computedStyle = getComputedStyle(element)
  if (computedStyle.display === 'none') return

  await to(
    element,
    {
      height: '0',
      paddingTop: '0',
      paddingBottom: '0',
      marginTop: '0',
      marginBottom: '0',
      borderTopWidth: '0',
      borderBottomWidth: '0',
    },
    {
      duration,
      easing,
      queue,
      onBeforeAnimate() {
        element.style.overflow = 'hidden'
      },
      onComplete() {
        element.style.removeProperty('overflow')
        element.style.display = 'none'
      },
      onUnsupported() {
        element.style.display = 'none'
      },
    },
  )
}

export async function slideDown(
  element,
  { duration = 600, display = 'block', easing = 'ease', queue } = {},
) {
  await from(
    element,
    {
      height: '0',
      paddingTop: '0',
      paddingBottom: '0',
      marginTop: '0',
      marginBottom: '0',
      borderTopWidth: '0',
      borderBottomWidth: '0',
    },
    {
      duration,
      easing,
      queue,
      onCapture() {
        element.style.display = display
      },
      onBeforeAnimate() {
        element.style.overflow = 'hidden'
      },
      onComplete() {
        element.style.removeProperty('overflow')
      },
      onUnsupported() {
        element.style.display = display
      },
    },
  )
}

export function step(duration, callback, { throttle = 0 } = {}) {
  let canceled = false
  let paused = false
  let finished = false
  let pauseProgress
  let startTimestamp = Date.now()
  let endTimestamp = startTimestamp + duration
  let previousCallTimestamp = -Infinity

  function getProgress() {
    if (paused) {
      return pauseProgress
    } else {
      return Math.min(1 - (endTimestamp - Date.now()) / duration, 1)
    }
  }

  let rafId
  const rafCallback = () => {
    if (paused) return

    let now = Date.now()

    if (throttle === 0 || previousCallTimestamp + throttle < now) {
      let progress = getProgress()

      if (progress === 1) {
        finished = true
      }

      callback(progress)
      previousCallTimestamp = now
    }

    if (!finished) {
      rafId = requestAnimationFrame(rafCallback)
    } else if (throttle > 0 && previousCallTimestamp !== now) {
      // Ensure that throttled calls are finished
      finished = true
      callback(1)
    }
  }

  rafId = requestAnimationFrame(rafCallback)

  return {
    pause() {
      if (finished || canceled) return
      if (paused) return
      pauseProgress = getProgress()
      paused = true
    },
    resume() {
      if (finished || canceled) return
      if (!paused) return
      startTimestamp = Date.now() - getProgress() * duration
      endTimestamp = startTimestamp + duration
      pauseProgress = undefined
      paused = false
      rafId = requestAnimationFrame(rafCallback)
    },
    cancel() {
      canceled = true
      cancelAnimationFrame(rafId)
    },
    get isCanceled() {
      return canceled
    },
    get isFinished() {
      return finished
    },
    get isPaused() {
      return !finished && !canceled && paused
    },
    get progress() {
      return getProgress()
    },
  }
}
